import React from 'react';
import {
  Box, Paper, Typography, Chip, Divider, Stack, Grid, Link,
} from '@mui/material';
import { CheckCircle as CheckIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { TrainingInsights, scoreColor } from '../types';
import SectionShell from './SectionShell';

interface Props {
  data: TrainingInsights['objectionFaqHandling'];
  /** Jump the conversation timeline to a given ISO timestamp. */
  onNavigateToTimestamp?: (timestampIso: string) => void;
}

const ObjFaqHandlingSection: React.FC<Props> = ({ data, onNavigateToTimestamp }) => {
  if (!data) return null;

  return (
    <SectionShell title="Objection & FAQ Handling" score={data.overallScore ?? null} summary={data.overallSummary}>

      <Stack spacing={2}>
        {data.items?.map((item, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
              <Chip
                label={item.itemType}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${item.handlingScore}%`}
                size="small"
                color={scoreColor(item.handlingScore)}
              />
              <Chip
                label={item.adherenceLabel}
                size="small"
                variant="outlined"
              />
              {item.timestampIso && onNavigateToTimestamp && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => onNavigateToTimestamp(item.timestampIso!)}
                  sx={{ ml: 'auto' }}
                >
                  View in timeline &rarr;
                </Link>
              )}
            </Box>
            <Typography variant="subtitle2" gutterBottom>
              {item.question}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {item.adherenceSummary}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Grid container spacing={2}>
              {item.whatWorked && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <CheckIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="success.main" fontWeight={600}>
                        What worked
                      </Typography>
                      <Typography variant="body2">{item.whatWorked}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {item.whatMissed && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <CancelIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="error.main" fontWeight={600}>
                        What missed
                      </Typography>
                      <Typography variant="body2">{item.whatMissed}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>

            {item.whatRepShouldHaveSaid && (
              <Box mt={1.5} p={1.5} bgcolor="var(--info-tint)" borderRadius={1}>
                <Typography variant="caption" color="info.main" fontWeight={600}>
                  Suggested response
                </Typography>
                <Typography variant="body2">
                  {item.whatRepShouldHaveSaid}
                </Typography>
              </Box>
            )}
          </Paper>
        ))}
      </Stack>
    </SectionShell>
  );
};

export default ObjFaqHandlingSection;
